import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'
import { FileObject, FilePurpose, StockImage } from '@citruscamps/citrus-client'
import { LoadingButton } from '../../LoadingButton/LoadingButton'
import { useFetchStockImages } from '../hooks/useFetchStockImages'
import { ImagePicker } from './ImagePicker'

interface IProps {
  value?: FileObject
  purpose?: FilePurpose
  programId: string
  onSelect?: (file?: FileObject) => void | Promise<void>
}

export const MediaStockView = ({
  value,
  purpose = 'other',
  programId,
  onSelect: handleSelect,
}: IProps) => {
  const [selected, setSelected] = useState<StockImage>()
  const [isAddingImage, setIsAddingImage] = useState<boolean>(false)
  const { data, isLoading, pagination, search, setPagination, handleAddImage, setSearch } =
    useFetchStockImages({
      programId,
      fetchProps: {
        sort: 'popular',
        order: 'DESC',
        filter: {},
      },
    })
  const toFileObject = (img: StockImage): FileObject => ({
    id: img.id,
    object: 'file',
    purpose,
    access: 'public',
    title: img.description,
    filename: '',
    filetype: 'image/jpeg',
    url: img.url,
    context: programId,
    size: 0,
    created: img.created,
    created_by: img.created_by.id,
  })

  return (
    <div className="mt-3">
      {!!selected && (
        <div className="row justify-content-center">
          <div className="col-12 col-sm-6">
            <div className="d-flex justify-content-between mt-2">
              <div className="d-flex small">
                <div>
                  <img
                    className="mt-1 me-2 rounded"
                    src={selected.created_by.profile_image}
                    alt={selected.created_by.name}
                    height={32}
                    width={32}
                  />
                </div>
                <div>
                  <div className="fw-bold">
                    <a
                      href={selected.created_by.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-body"
                    >
                      {selected.created_by.name}
                    </a>
                  </div>
                  <div>
                    <a
                      href={selected.created_by.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-body"
                    >
                      @{selected.created_by.username}
                    </a>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-link text-body"
                onClick={async () => {
                  setSelected(undefined)
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="mt-2"></div>
            <div className="d-flex mt-2">
              <img
                className="img-fluid img-thumbnail mx-auto"
                src={selected.url}
                alt={selected.description}
              />
            </div>
            <div className="mt-2">
              <LoadingButton
                type="button"
                className="btn btn-primary btn-block"
                onClick={async () => {
                  setIsAddingImage(true)
                  try {
                    const item = await handleAddImage({
                      id: selected.id,
                      source: 'unsplash',
                      purpose,
                      context: programId,
                    })
                    await handleSelect?.(item)
                    setIsAddingImage(false)
                  } catch (e: any) {
                    setIsAddingImage(false)
                  }
                }}
                loading={isAddingImage}
              >
                Add image
              </LoadingButton>
            </div>
          </div>
          <div className="col-12 col-sm-8">
            <div className="text-body-secondary small mt-2">
              This image is provided by a third party called Unsplash; it is not provided by Citrus.
              By clicking "Add image", you are agreeing to{' '}
              <a href="https://unsplash.com/terms" target="_blank" rel="noopener noreferrer">
                Unsplash's Terms
              </a>{' '}
              and you understand that you are publishing the image and are responsible for it.
            </div>
          </div>
        </div>
      )}

      {!selected && (
        <>
          <div className="mx-3 mb-2">
            <input
              className="form-control rounded"
              placeholder="Search for images"
              value={search || ''}
              onChange={(evt: any) => {
                setSearch(evt.target.value)
              }}
            />
            <div className="small text-body-secondary text-end">
              Powered by{' '}
              <a
                href="https://img.com/?utm_source=citrus&medium=referral"
                target="_blank"
                rel="noreferrer"
              >
                Unsplash
              </a>
            </div>
          </div>
          <ImagePicker
            value={selected ? toFileObject(selected) : undefined}
            images={data.map(toFileObject)}
            loading={isLoading}
            pagination={pagination}
            onChangePage={setPagination}
            onSelect={(item) => {
              setSelected(data.find((d) => d.id === item?.id))
            }}
          />
        </>
      )}
    </div>
  )
}
